import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewClient() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API}/clients`,
        {
          name,
          email: email || undefined,
          phone: phone || undefined,
          session_type: sessionType || undefined,
          status: 'lead',
          priority: 'medium',
        },
        { withCredentials: true }
      );
      navigate(`/clients/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-2 text-text-secondary hover:text-foreground mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <h1 className="text-3xl font-serif font-bold text-foreground mb-6" data-testid="new-client-title">
          New Client
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="Client name"
              data-testid="client-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="client@example.com"
              data-testid="client-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="+1 234 567 8900"
              data-testid="client-phone-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Session Type
            </label>
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="client-session-type-select"
            >
              <option value="">Select type...</option>
              <option value="portrait">Portrait</option>
              <option value="content_creator">Content Creator</option>
              <option value="editorial">Editorial</option>
              <option value="commercial">Commercial</option>
              <option value="event">Event</option>
              <option value="other">Other</option>
            </select>
          </div>

          {error && (
            <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/clients')}
              className="flex-1 bg-secondary text-foreground hover:bg-secondary/80 border border-border rounded-lg px-4 py-2 font-medium transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-background hover:bg-primary/90 rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
